import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { Zap, DollarSign, TrendingUp, Activity, Calendar } from 'lucide-react';
import BACKEND_URL from '../config/api';

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const TokenUsageSection = ({ userId }) => {
  const [stats, setStats] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30); // days

  useEffect(() => {
    fetchTokenStats();
  }, [timeRange]);

  const fetchTokenStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // Fetch overall stats
      const statsResponse = await fetch(`${BACKEND_URL}/api/token-usage/user/stats?days=${timeRange}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const statsData = await statsResponse.json();
      setStats(statsResponse.ok && typeof statsData.total_tokens === 'number' ? statsData : null);

      // Fetch daily usage
      const dailyResponse = await fetch(`${BACKEND_URL}/api/token-usage/user/daily?days=${Math.min(timeRange, 30)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const dailyData = await dailyResponse.json();
      setDailyData(dailyData.daily_usage || []);

    } catch (error) {
      console.error('Error fetching token stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Zap className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No token usage data available yet.</p>
          <p className="text-sm text-gray-500 mt-2">Start using AI features to see your usage statistics.</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare data for charts
  const featureData = Object.entries(stats.by_feature || {}).map(([feature, data]) => ({
    name: feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    tokens: data.tokens,
    cost: data.cost,
    count: data.count
  }));

  const modelData = Object.entries(stats.by_model || {}).map(([model, data]) => ({
    name: model,
    tokens: data.tokens,
    cost: data.cost
  }));

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-900">Token Usage Analytics</h3>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value={7}>Last 7 Days</option>
          <option value={30}>Last 30 Days</option>
          <option value={90}>Last 90 Days</option>
          <option value={0}>All Time</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <Zap className="w-4 h-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_tokens.toLocaleString()}</div>
            <p className="text-xs text-gray-500 mt-1">Across all features</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${stats.total_cost_usd.toFixed(4)}</div>
            <p className="text-xs text-gray-500 mt-1">Estimated</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Searches</CardTitle>
            <Activity className="w-4 h-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_searches}</div>
            <p className="text-xs text-gray-500 mt-1">AI operations</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg per Search</CardTitle>
            <TrendingUp className="w-4 h-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.total_searches > 0 ? Math.round(stats.total_tokens / stats.total_searches).toLocaleString() : 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">tokens</p>
          </CardContent>
        </Card>
      </div>

      {/* Daily Usage Chart */}
      {dailyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Daily Token Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tokens" stroke="#8b5cf6" strokeWidth={2} name="Tokens" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Feature Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Usage by Feature</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={featureData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="tokens"
                >
                  {featureData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tokens by Feature</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={featureData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="tokens" fill="#8b5cf6" name="Tokens" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Table */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Feature</th>
                  <th className="text-right py-3 px-4">Tokens</th>
                  <th className="text-right py-3 px-4">Cost</th>
                  <th className="text-right py-3 px-4">Operations</th>
                  <th className="text-right py-3 px-4">Avg/Operation</th>
                </tr>
              </thead>
              <tbody>
                {featureData.map((feature, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 font-medium">{feature.name}</td>
                    <td className="text-right py-3 px-4">{feature.tokens.toLocaleString()}</td>
                    <td className="text-right py-3 px-4">${feature.cost.toFixed(6)}</td>
                    <td className="text-right py-3 px-4">{feature.count}</td>
                    <td className="text-right py-3 px-4">{Math.round(feature.tokens / feature.count).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TokenUsageSection;
